import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import type { AlarmState, PipelineHealthResponse } from '../../types/ai-pipeline';

export interface HealthPanelProps {
  readonly health: PipelineHealthResponse | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
}

/** Maps alarm state to its display configuration. */
function getAlarmDisplay(state: AlarmState['state']): {
  icon: string;
  label: string;
  dotClass: string;
  textClass: string;
} {
  switch (state) {
    case 'OK':
      return { icon: '●', label: 'OK', dotClass: 'text-green-600', textClass: 'text-green-700' };
    case 'ALARM':
      return { icon: '⚠', label: 'ALARM', dotClass: 'text-red-600', textClass: 'text-red-700 font-bold' };
    case 'INSUFFICIENT_DATA':
      return { icon: '?', label: 'Insufficient Data', dotClass: 'text-gray-500', textClass: 'text-gray-600' };
    case 'UNKNOWN':
    default:
      return { icon: '—', label: 'Unknown', dotClass: 'text-gray-500', textClass: 'text-gray-600' };
  }
}

function AlarmRow({ alarm }: { readonly alarm: AlarmState }): React.ReactElement {
  const display = getAlarmDisplay(alarm.state);

  return (
    <li className="flex items-center gap-3 py-2 px-3 rounded-md border border-border">
      <span
        className={`text-lg ${display.dotClass}`}
        aria-hidden="true"
      >
        {display.icon}
      </span>
      <span className="flex-1 text-sm font-medium">{alarm.displayName}</span>
      <span
        className={`text-sm ${display.textClass}`}
        aria-label={`${alarm.displayName} status: ${display.label}`}
      >
        {display.label}
      </span>
    </li>
  );
}

/**
 * Displays pipeline health based on CloudWatch alarm states.
 * Shows an aggregate health badge and individual alarm indicators
 * with accessible text and icons (not relying on color alone).
 */
export function HealthPanel({ health, isLoading, error, onRetry }: HealthPanelProps): React.ReactElement {
  if (error) {
    return (
      <Alert variant="destructive" aria-live="assertive">
        <AlertTitle>Failed to load health data</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>{error}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading && !health) {
    return (
      <div className="flex items-center gap-2 p-4" role="status" aria-live="polite">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
        <span className="text-sm text-muted-foreground">Loading health data…</span>
      </div>
    );
  }

  if (!health) {
    return (
      <p className="text-sm text-muted-foreground p-4">No health data available.</p>
    );
  }

  const isHealthy = health.aggregateHealthy;

  return (
    <div aria-live="polite" aria-label="Pipeline health status">
      {/* Aggregate health badge */}
      <div className="mb-4">
        {isHealthy ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800"
            aria-label="Pipeline health: Healthy"
          >
            <span aria-hidden="true">✓</span> Healthy
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800"
            aria-label="Pipeline health: Degraded"
          >
            <span aria-hidden="true">⚠</span> Degraded
          </span>
        )}
      </div>

      {/* Alarm list */}
      <ul className="space-y-2" aria-label="CloudWatch alarm states">
        {health.alarms.map((alarm) => (
          <AlarmRow key={alarm.alarmName} alarm={alarm} />
        ))}
      </ul>
    </div>
  );
}
